import AsyncSelect from "react-select/async";
import { components, OptionProps, SingleValueProps } from "react-select";
import { useMemo, useState } from "react";
import { odooClient } from "../services/odooClient";

/* ================= TYPES ================= */

export type UserOption = {
  id: number | string;
  name: string;
  country?: string;
  tag?: string;
  avatar?: string;
  email?: string;
  /**
   * Source model when the option was loaded from a contact. We deliberately
   * keep this to the two CRM contact models so downstream consumers
   * (proposal counter, CRM pipeline sync, etc.) don't need to add a third
   * branch. Internal staff loaded from `res.users` leave this `undefined`
   * and are identified instead via `isInternal`.
   */
  _model?: "crm.prospect" | "res.partner";
  /**
   * `true` when this option represents an Opera Gallery internal staff user
   * (loaded from `res.users` via `fetchUsers`). Lets callers route the email
   * differently (e.g. send a dashboard link instead of a PDF attachment).
   */
  isInternal?: boolean;
};

type ViewMode = "info" | "email";

type Props = {
  value: UserOption | null;
  onChange: (user: UserOption | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  defaultViewMode?: ViewMode;
  selectOnlyWithEmail?: boolean;
  /**
   * When `true`, the dropdown additionally surfaces Opera Gallery internal
   * staff (loaded from `res.users`, filtered to `@operagallery.com`) on top
   * of the regular contact search. Default `false` — most call sites only
   * want clients/prospects.
   */
  includeInternalUsers?: boolean;
};

/* ================= HELPERS ================= */

const INTERNAL_DOMAIN = "operagallery.com";

const isInternalEmail = (email?: string | null): boolean => {
  if (!email) return false;
  const norm = String(email).trim().toLowerCase();
  return norm.endsWith(`@${INTERNAL_DOMAIN}`);
};

const fetchContacts = async (
  query: string,
  includeInternalUsers: boolean,
): Promise<UserOption[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  // Two parallel searches — `searchContacts` returns clients/prospects and
  // explicitly filters out portal users (`is_user = false`), so we need a
  // separate `res.users` query to surface internal staff. We always request
  // both when the caller opts in; merging happens below.
  const [contacts, internalUsers] = await Promise.all([
    odooClient.searchContacts(trimmed, 20).catch((err: any) => {
      console.warn("UserSearchSelect: searchContacts failed", err);
      return [] as any[];
    }),
    includeInternalUsers
      ? odooClient
          .fetchUsers({ search: trimmed, page: 1, pageSize: 20 })
          .catch((err: any) => {
            console.warn("UserSearchSelect: fetchUsers failed", err);
            return [] as any[];
          })
      : Promise.resolve([] as any[]),
  ]);

  const mappedContacts: UserOption[] = (contacts || []).map((c: any) => ({
    id: c.id,
    name: c.name,
    country: c.countries_Country,
    tag: c.category ?? "VIP Collector",
    email: c.email || c.Email1 || c.Email2 || undefined,
    avatar: c.image_128 ? `data:image/png;base64,${c.image_128}` : undefined,
    _model: c._model as "crm.prospect" | "res.partner" | undefined,
    isInternal: isInternalEmail(c.email || c.Email1 || c.Email2),
  }));

  const mappedUsers: UserOption[] = (internalUsers || [])
    // `fetchUsers` returns every active res.user; we only want @operagallery
    // ones in the picker so non-Opera portal accounts (rare but possible)
    // don't pollute the dropdown.
    .filter((u: any) => isInternalEmail(u?.email || u?.login))
    .map((u: any) => ({
      // Prefix the id so internal users can't collide with res.partner ids
      // when stored on a single chip / payload that doesn't carry _model.
      // We intentionally leave `_model` undefined — internal users are not
      // CRM contacts, so we don't want proposal-counter or pipeline-sync
      // code paths treating them as one. Use `isInternal` to detect them.
      id: `user-${u.id}`,
      name: u.name || u.login || `User ${u.id}`,
      tag: "Internal",
      email: u.email || u.login || undefined,
      isInternal: true,
    }));

  // Internal users surfaced first when present so a colleague typing a
  // teammate's name doesn't have to scroll past identically-named contacts.
  return [...mappedUsers, ...mappedContacts];
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/* ================= CUSTOM OPTION ================= */

const Option = (
  props: OptionProps<UserOption, false> & { viewMode: ViewMode }
) => {
  const { data, viewMode } = props;

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-950/50 text-sm font-semibold text-indigo-300">
          {data.avatar ? (
            <img
              src={data.avatar}
              alt={data.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            getInitials(data.name)
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col">
          <span className="flex items-center gap-2 text-sm font-medium text-white">
            {data.name}
            {data.isInternal && (
              <span className="rounded-full bg-emerald-950/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-emerald-700">
                Internal
              </span>
            )}
          </span>

          {viewMode === "email" ? (
            <span className="text-xs text-[#999999]">
              {data.email ?? (
                <span className="italic text-[#666666]">No email</span>
              )}
            </span>
          ) : (
            (
              <span className="text-xs text-[#999999]">
                {data.country}
                {data.country && data.tag && " • "}
                {data.tag}
              </span>
            )
          )}
        </div>
      </div>
    </components.Option>
  );
};

/* ================= SELECTED VALUE ================= */

const SingleValue = (
  props: SingleValueProps<UserOption> & { viewMode: ViewMode }
) => {
  const { data, viewMode } = props;

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-950/50 text-xs font-semibold text-indigo-300">
          {data.avatar ? (
            <img
              src={data.avatar}
              alt={data.name}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            getInitials(data.name)
          )}
        </div>
        <span className="text-sm text-white">{data.name}</span>
        {viewMode === "email" && data.email && (
          <span className="text-xs text-[#999999]">{data.email}</span>
        )}
      </div>
    </components.SingleValue>
  );
};

/* ================= MAIN COMPONENT ================= */

export default function UserAsyncSelect({
  value,
  onChange,
  placeholder = "Search user...",
  isClearable = true,
  defaultViewMode = "email",
  selectOnlyWithEmail = false,
  includeInternalUsers = false,
}: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [onlyWithEmail, setOnlyWithEmail] = useState(selectOnlyWithEmail);

  // selectKey forces AsyncSelect to remount (clears cache) when filters change
  const selectKey = `${viewMode}-${onlyWithEmail}-${includeInternalUsers}`;

  const loadOptions = useMemo(
    () => (query: string) => fetchContacts(query, includeInternalUsers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlyWithEmail, includeInternalUsers],
  );

  const componentsMap = useMemo(
    () => ({
      Option: (props: OptionProps<UserOption, false>) => (
        <Option {...props} viewMode={viewMode} />
      ),
      SingleValue: (props: SingleValueProps<UserOption>) => (
        <SingleValue {...props} viewMode={viewMode} />
      ),
    }),
    [viewMode]
  );

  return (
    <div className="flex flex-col gap-2">

      {/* Select */}
      <AsyncSelect<UserOption, false>
        key={selectKey}
        value={value}
        onChange={(val) => {onChange(val)}}
        loadOptions={loadOptions}
        isOptionDisabled={(option) => onlyWithEmail && !option.email}
        placeholder={placeholder}
        isClearable={isClearable}
        cacheOptions
        defaultOptions={false}
        getOptionLabel={(u) => u.name}
        getOptionValue={(u) => String(u.id)}
        components={{
          ...componentsMap,
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        noOptionsMessage={() => "Enter keyword"}
        classNames={{
          control: ({ isFocused }) =>
            `min-h-[42px] rounded-lg border bg-[#0a0a0a] ${
              isFocused
                ? "border-[#c9a84c] ring-1 ring-[#c9a84c]"
                : "border-[#2a2a2a]"
            }`,
          input: () => "text-white",
          singleValue: () => "text-white",
          option: ({ isFocused, isSelected }) =>
            `cursor-pointer px-2 py-2 ${
              isSelected ? "bg-[#2a2418]" : isFocused ? "bg-[#1a1a1a]" : "bg-[#111111]"
            }`,
          menu: () => "mt-1 rounded-lg border border-[#2a2a2a] bg-[#111111] shadow-lg",
          placeholder: () => "text-sm text-[#666666]",
        }}
      />
    </div>
  );
}